import { useContext } from 'react';
import * as React from 'react';

import type { TranscriptionResponseDTO } from '@klangio/ai2notes-ts-client';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ExportType, JobType } from '../../../entities';
import MenuItemMouseOver from '../../atoms/MenuItemMouseOver';
import { AppConstantsContext, UserContext } from '../../../context';

export type DownloadMenuVariant = 'songbook' | 'sheet';

export interface DropDownContentProps {
  handleDownloadClick: (type: ExportType) => void;

  transcription: TranscriptionResponseDTO;
  handleUnlockTranscription: (type: ExportType) => void;
}

interface MenuItemConfig {
  type: ExportType;
  translationKey: string;
  condition?: (props: {
    isAdmin: boolean;
    transcription: TranscriptionResponseDTO;
    jobType: JobType;
  }) => boolean;
}

const MENU_ITEMS_CONFIG: MenuItemConfig[] = [
  { type: ExportType.PDF, translationKey: 'home.songbook.download_pdf' },
  { type: ExportType.MIDI, translationKey: 'home.songbook.download_midi' },
  {
    type: ExportType.UNQUANTIZED_MIDI,
    translationKey: 'home.songbook.download_unquant_midi',
    condition: ({ jobType }) => jobType !== JobType.TranscribeScan,
  },
  { type: ExportType.MUSIC_XML, translationKey: 'home.songbook.download_music_xml' },
  { type: ExportType.LILYPOND, translationKey: 'home.songbook.download_lilypond' },
  {
    type: ExportType.MP3,
    translationKey: 'home.songbook.download_mp3',
    condition: ({ isAdmin, transcription }) => transcription.source === 'microphone' || isAdmin,
  },
  {
    type: ExportType.GUITAR_PRO,
    translationKey: 'home.songbook.download_guitar_pro',
    condition: ({ jobType }) =>
      jobType === JobType.TranscribeGuitar ||
      jobType === JobType.TranscribeScan ||
      jobType === JobType.TranscribeStudio,
  },
  {
    type: ExportType.PIANO_ROLL,
    translationKey: 'home.songbook.download_piano_roll',
    condition: ({ isAdmin }) => isAdmin,
  },
  {
    type: ExportType.MUSIC_JSON,
    translationKey: 'Export MusicJson',
    condition: ({ isAdmin }) => isAdmin,
  },
];

const DropDownContent: React.FC<DropDownContentProps> = ({
  handleDownloadClick,
  transcription,
  handleUnlockTranscription,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { jobType } = useContext(AppConstantsContext);
  const isAdmin = user?.role === 'admin';

  const renderMenuItem = (config: MenuItemConfig) => {
    const { type, translationKey, condition } = config;

    const shouldShow = !condition || condition({ isAdmin, transcription, jobType });
    if (!shouldShow) return null;

    if (transcription?.demo && type !== ExportType.PDF) {
      return (
        <MenuItemMouseOver
          key={type}
          handleUnlockTranscription={() => handleUnlockTranscription(type)}
          mouseOverObject={(
            <>
              <LockOpenIcon />
              {' '}
              <div>{t('common.upgrade')}</div>
            </>
          )}
          menuText={t(translationKey)}
        />
      );
    }

    return (
      <MenuItem key={type} onClick={() => handleDownloadClick(type)}>
        {t(translationKey)}
      </MenuItem>
    );
  };

  return <div>{MENU_ITEMS_CONFIG.map(renderMenuItem)}</div>;
};

export default DropDownContent;
